import { useCallback, useRef, useState } from "react";
import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import { UserWithMediaAndGroups } from "@greenflagdate/shared";
import { apiReq } from "../../../lib/apiReq";
import { store } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { toaster } from "../../../lib/toaster";
import { Icon } from "../../../components/Icon/Icon";
import { ModalPage } from "../../../components/ModalPage";
import {
  PaymentForm,
  PaymentFormHandle,
} from "../../../components/PaymentForm/PaymentForm";

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text
      as="div"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="fg.muted"
      fontWeight="semibold"
      mb="10px"
    >
      {children}
    </Text>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="sm"
      boxShadow="card"
      overflow="hidden"
      p="16px"
    >
      {children}
    </Box>
  );
}

export function Billing() {
  const paymentFormRef = useRef<PaymentFormHandle>(null);
  const [editingCard, setEditingCard] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [deleteCardReq, deleteCardReqState] =
    useReq<UserWithMediaAndGroups>(apiReq);

  const handleRemoveCard = useCallback(async () => {
    const response = await deleteCardReq.delete("/user/card", {});
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      setConfirmingRemove(false);
      paymentFormRef.current?.reloadCard();
      toaster.create({ type: "success", title: "Card removed." });
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't remove your card. Please try again.",
      });
    }
  }, [deleteCardReq]);

  return (
    <ModalPage
      motionKey="settings-billing-modal"
      title="Billing"
      fallback="/settings"
      bodyBg="bg.canvas"
    >
      <Flex direction="column" minH="100%" w="100%">
        <Box flex="1" w="100%" maxW="40rem" mx="auto" pt="18px" pb="2rem">
          <Box px="4" pt="4">
            <Eyebrow>Payment Method</Eyebrow>
            <Card>
              <PaymentForm
                ref={paymentFormRef}
                onEditCardChange={setEditingCard}
                onLoadingStatusChange={setSavingCard}
              />
              {editingCard && (
                <Box mt="16px">
                  <Button
                    bg="bg.brand"
                    color="fg.onBrand"
                    loading={savingCard}
                    onClick={() => paymentFormRef.current?.submitForm()}
                  >
                    Save card
                  </Button>
                </Box>
              )}
              {!editingCard && (
                <Box mt="16px" pt="16px" borderTop="1px solid" borderColor="border.subtle">
                  {confirmingRemove ? (
                    <Box>
                      <Text fontSize="13px" color="fg.default" mb="12px">
                        Remove this card? You'll need to add a new one to
                        register for events.
                      </Text>
                      <HStack gap="2">
                        <Button
                          size="sm"
                          bg="bg.danger"
                          color="fg.onBrand"
                          loading={deleteCardReqState.loading}
                          onClick={handleRemoveCard}
                        >
                          Remove card
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmingRemove(false)}
                          disabled={deleteCardReqState.loading}
                        >
                          Cancel
                        </Button>
                      </HStack>
                    </Box>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      color="fg.danger"
                      boxShadow="inset 0 0 0 1px var(--chakra-colors-fg-danger)"
                      onClick={() => setConfirmingRemove(true)}
                    >
                      <Icon name="x" boxSize="12px" />
                      Remove card
                    </Button>
                  )}
                </Box>
              )}
            </Card>
          </Box>
        </Box>
      </Flex>
    </ModalPage>
  );
}
